import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { isPermissionsEnabled } from "../../app/utils";
import { selectPermissions } from "./permissionsSlice";

/**
 * Reads the permission set. `can` is deliberately permissive in every state where the answer is not
 * known, and the reasoning is worth keeping because it looks like a security decision and is not.
 *
 * Gating here is usability: every staff account still holds the legacy `admin` role, so direct API
 * calls remain exactly as broad as they were before any of this. Hiding a button prevents somebody
 * starting work the server would refuse; it does not prevent the work. Given that, failing closed
 * when the permission set is unknown would turn one flaky request into a portal-wide outage while
 * protecting nothing. So:
 *
 * - flag off - everything is permitted, which is today's behaviour and the whole point of the flag;
 * - still loading - permitted, and callers that would otherwise flash a refusal check `settled`;
 * - the request failed - permitted, and `error` is surfaced so a permanently-failing endpoint is
 *   visible rather than quietly disabling every gate in the portal.
 */
export const usePermissions = () => {
  const state = useSelector(selectPermissions);
  const enabled = isPermissionsEnabled();

  const held = useMemo(() => new Set(state.permissions ?? []), [state.permissions]);
  const settled = state.status === "succeeded" || state.status === "failed";
  const gating = enabled && state.status === "succeeded";

  const can = useCallback((key) => (gating && key ? held.has(key) : true), [gating, held]);

  return {
    can,
    enabled,
    /** True once an answer exists, whether it arrived or failed. Callers wait on this before refusing. */
    settled: enabled ? settled : true,
    gating,
    status: state.status,
    error: state.error,
    permissions: state.permissions ?? [],
    roles: state.roles ?? [],
    primaryRole: state.primaryRole,
    accountType: state.accountType,
  };
};
